<template>
  <section class="basket">
    <ModalAsideHeader>
      {{ isCheckoutPage ? messages.editOrder : messages.title }}
      <span v-if="items && !isCheckoutPage" class="basket__count">{{ items.length }}</span>
    </ModalAsideHeader>
    <ModalAsideBody>
      <div class="basket-body">
        <div v-for="(item, i) in getItems">
          <BasketProduct
            v-if="!item.removed"
            :key="getProductId(item)"
            :result="item"
            :data-id="getProductId(item)"
            :order-number="i"
            :change="handlerChangeSKU"
            @remove="handlerRemove(i, $event)"
          />

          <div v-else :key="item.id" class="basket__recovery">
            <span>{{ messages.productWasRemoved }}</span>
            <DefLink @click="handlerRecovery(i, removedItems[i])">
              {{ messages.rebuild + '?' }}
            </DefLink>
          </div>
        </div>
        <BasketKits />
      </div>
    </ModalAsideBody>
    <ModalAsideBottom>
      <div class="basket-bottom">
        <div class="basket__confirmation">
          <BasketTotal />
          <DefButtonPrimary :href="createOrder.path">
            {{ createOrder.name }}
          </DefButtonPrimary>
        </div>
      </div>
    </ModalAsideBottom>
  </section>
</template>

<script>
import BasketProduct from '@components/Basket/BasketProduct/BasketProduct.vue';
import DefLink from '@components/Def/DefLink/DefLink.vue';
import BasketTotal from '@components/Basket/BasketTotal.vue';
import DefButtonPrimary from '@components/Def/DefButtonPrimary/DefButtonPrimary.vue';
import { mapState, mapMutations, mapActions, mapGetters } from 'vuex';
import useScreenWidth from '@use/useScreenWidth';
import BasketKits from '@components/Basket/BasketKits/BasketKits.vue';
import Analytics from '@/utils/Analytics';
import ModalAsideHeader from '@use/useModalAside/ModalAsideHeader.vue';
import ModalAsideBottom from '@use/useModalAside/ModalAsideBottom.vue';
import ModalAsideBody from '@use/useModalAside/ModalAsideBody.vue';

export default {
  name: 'Basket',
  components: {
    ModalAsideBody,
    ModalAsideBottom,
    ModalAsideHeader,
    BasketKits,
    BasketProduct,
    DefLink,
    BasketTotal,
    DefButtonPrimary,
  },
  data() {
    return {
      removedItems: {},
      animatedHeight: '',
      screenWidth: useScreenWidth(),
    };
  },
  computed: {
    ...mapState({
      items: ({ basket }) => basket.data?.items,
      basket: ({ basket }) => basket.data,
      messages: ({ basket }) => basket.data?.messages,
      createOrder: ({ basket }) => basket.data?.routes?.createOrder,
      isAvailabilityIn1c: ({ checkout }) => checkout?.data?.isAvailabilityIn1c,
    }),
    ...mapGetters({
      getProductById: 'basket/getProductById',
    }),
    getItems() {
      try {
        /**
         * if element is delete will show recovery message
         * if element s return show elements in right order
         * * */
        // console.log(this.items);
        const copyItems = this.items ? [...this.items] : [];

        for (const key in this.removedItems) {
          const index = copyItems.findIndex(
            (el) => (el?.offer?.id || el?.id) == this.removedItems[key].id
          );

          if (index === -1) {
            copyItems.splice(key, 0, { removed: true, id: this.removedItems[key].id });
          } else {
            const item = { ...copyItems[index] };
            copyItems.splice(index, 1);
            copyItems.splice(key, 0, item);
          }
        }

        return copyItems;
      } catch (err) {
        console.error(err);
        return this.items;
      }
    },
    isElectronicCertificate() {
      return !!this.getItems.find((el) => el?.isElectronicCertificate);
    },
    isCheckoutPage() {
      return this.$route.path.match(/^(\/[A-Za-z]{2})?\/personal\/order\/make\//);
    },
  },
  beforeMount() {
    Analytics.viewBasket(this.items, {
      currency: 'UAH',
      value: this.basket.price.price,
    });
  },
  methods: {
    ...mapMutations({
      setAvailabilityIn1c: 'checkout/setAvailabilityIn1c',
    }),
    ...mapActions({
      remove: 'basket/remove',
      removeBox: 'basket/removeBox',
      add: 'basket/add',
      isProductAvailableIn1C: 'checkout/isProductAvailableIn1C',
    }),
    getProductId({ id, offer }) {
      return offer?.id ?? id;
    },
    async handlerRemove(key, product) {
      const id = product?.offer?.id || product?.id;

      await this.remove({
        id,
        basketId: product.basket_id,
      });

      this.removedItems[key] = {
        id,
        count: product.count,
      };

      Analytics.removeFromBasket(product);
    },
    async handlerRecovery(key, product) {
      const { id, count } = product;
      await this.add({ id, count });
      Analytics.addToBasket(this.getProductById(id));
    },
    async handlerChangeSKU(key, item) {
      const newId = item?.offer?.id ?? item.offer;

      if (key in this.removedItems) {
        this.removedItems[key] = {
          id: newId,
          count: item.count,
        };
      }
      /** request + updating 1c availability for product (mechanic war transfer from backend) */
      if (this.isCheckoutPage) {
        await this.isProductAvailableIn1C({
          ...item,
          useLoader: true,
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.basket {
  display: flex;
  flex-direction: column;
  height: 100%;
  &__title {
    font-weight: 600;
    font-size: rem(14);
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #030304;
    margin-bottom: 0;
    @include point-md {
      font-size: rem(24);
    }
  }
  &__count {
    color: gray;
  }
  &__confirmation {
    display: flex;
    flex-direction: column;
    @include point-md {
      grid-column-start: 2;
      grid-column-end: 3;
    }
  }
  &__recovery {
    padding: 24px 0;
    font-weight: 500;
    font-size: rem(14);
    line-height: 125%;
    color: var(--color-dark-grey);
    border-bottom: 1px solid var(--border-grey);
    :deep(.def-link) {
      margin-left: 8px;
    }
  }
  :deep(.basket-total) {
    margin-bottom: 14px;
  }
}
.basket-body {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-row-gap: 16px;
}
.basket-bottom {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  align-items: flex-end;
  @include point-md {
    grid-column-gap: 32px;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }
  @include point-lg {
    grid-column-gap: 60px;
  }
}
</style>
